import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface CandidateResponse {
  id: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  employment_type: string;
  work_mode?: string;
  experience_level: string;
  experience_environment: string;
  years_of_experience: string;
  address: string | null;
  country: string | null;
  region: string | null;
  salary_expectation: string;
  role_source: string;
  assessment_response: number;
}

export interface EmployerExpectation {
  id: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  employment_type: string;
  work_mode?: string;
  experience_level: string;
  experience_environment: string;
  years_of_experience: string;
  address: string | null;
  country: string | null;
  region: string | null;
  salary_range_term: string;
  salary_range_currency: string;
  min_salary_range_amount: string;
  max_salary_range_amount: string;
  require_employment_type: boolean;
  require_work_mode?: boolean;
  require_experience_level: boolean;
  require_years_of_experience: boolean;
  require_work_environment: boolean;
  require_salary_range: boolean;
  require_cv_upload: boolean;
  assessment: number;
}

export interface CvRelevance {
  level: string;
  summary: string;
  percentage: number;
}

export interface ComparisonResult {
  result: 'match' | 'conflict';
  candidate: any;
  employer: any;
}

export interface Comparisons {
  experience_level?: ComparisonResult;
  years_of_experience?: ComparisonResult;
  employment_type?: ComparisonResult;
  work_mode?: ComparisonResult;
  salary_expectation?: ComparisonResult;
  experience_environment?: ComparisonResult;
}

export interface BasicExpectationData {
  score: number;
  candidate_response: CandidateResponse;
  employer_expectation: EmployerExpectation;
  comparisons: Comparisons;
  gaps: string[];
  strengths: string[];
  cv_relevance: CvRelevance;
  implications: string[];
}

interface BasicExpectationResponse {
  message: string;
  data: BasicExpectationData;
}

/**
 * Hook for fetching basic expectation data
 */
export const useBasicExpectation = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['basicExpectation', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<BasicExpectationResponse>(
        `/reports/${reportId}/basic-expectation/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch basic expectation';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    basicExpectation: data,
    isLoading,
    error,
    refetch,
  };
};
